/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { AntFlowJob } from '@/types/models';
import { WorkflowStep } from 'ogc-processes-components';
import { useEffect, useMemo, useState } from 'react';
import { DataGroupExtended, DataItemExtended, ProgressionState } from '../types';
import { DataItem } from 'vis-timeline';
import { classNames } from 'primereact/utils';
import { DateTime } from 'luxon';

type TraceDataType = {
  workflowSteps?: Array<WorkflowStep> | Record<string, WorkflowStep>;
  job: AntFlowJob;
  offsetMinute: number;
};

const IGNORED_NODE_NAMES = ['node_wf'];

const formatOriginalWftoArray = (workflowSteps: Array<WorkflowStep> | Record<string, WorkflowStep>) => {
  if (Array.isArray(workflowSteps) && workflowSteps.length !== 0) {
    return workflowSteps.map((el) => ({ id: el.id, label: el.label }));
  } else {
    const _steps = Object.entries(workflowSteps).map(([k, v]) => ({ id: k, label: v.label }));
    return _steps;
  }
};

export const useTraceData = ({ job, workflowSteps, offsetMinute }: TraceDataType) => {
  const [executionTrace, setExecutionTrace] = useState<ProgressionState[]>([]);
  const [items, setItems] = useState<DataItem[]>([]);
  const [groups, setGroups] = useState<DataGroupExtended[]>([]);

  const originalStepWithoutWrapping = useMemo(() => {
    if (workflowSteps) {
      return formatOriginalWftoArray(workflowSteps);
    } else {
      return Object.keys(job.progression_state || {}).map((key) => ({ id: key, label: key }));
    }
  }, [workflowSteps, job.progression_state]);

  useEffect(() => {
    if (!job.progression_state || !originalStepWithoutWrapping || originalStepWithoutWrapping.length === 0) {
      throw new Error('No workflow steps available to display in the timeline.');
    }

    const executionTrace: ProgressionState[] = [];
    Object.entries(job.progression_state).forEach(([key, steps_execution]) => {
      steps_execution.jobs.forEach((step) => {
        const item = {
          ...step,
          id: key,
          order: steps_execution.order,
          label: originalStepWithoutWrapping.find((el) => el.id === key)?.label || key
        } satisfies ProgressionState;
        executionTrace.push(item);
      });
    });
    const sortedExecutionTrace = executionTrace
      .filter((node) => !IGNORED_NODE_NAMES.includes(node.id))
      .sort((a, b) => (a.order ?? Infinity) - (b.order ?? Infinity));

    const items: DataItem[] = [];
    const groups: DataGroupExtended[] = [];

    originalStepWithoutWrapping.forEach((step, index) => {
      const referenceStep = sortedExecutionTrace.find((el) => el.id === step.id && el.status !== 'pending');
      if (referenceStep) {
        const group_item = {
          id: step.id ?? index,
          content: referenceStep.label || referenceStep.name,
          order: referenceStep.order ?? index,
          visible: true
        };
        groups.push(group_item as DataGroupExtended);
      }
    });

    sortedExecutionTrace
      .filter((e) => e.status !== 'pending')
      .forEach((step, idx) => {
        if (step.started) {
          const group = groups.find((g) => {
            const criteria = step.label || step.id;
            return g.content === criteria;
          });
          const statusTitle = step.status === 'completed' ? 'finished' : step.status;

          const item: DataItemExtended = {
            id: step.name || `item-${idx}`,
            content: step.name || '',
            start: new Date(step.started),
            group: group?.id,
            end: step.finished ? new Date(step.finished) : undefined,
            title: statusTitle,
            type: step.finished ? 'range' : 'box',
            className: classNames('timeline-item border-round-lg', {
              'timeline-item-running': job.status == 'running' ? step.status === 'running' : false,
              'timeline-item-completed ': step.status === 'completed',
              'timeline-item-failed':
                step.status === 'failed' || (job.status !== 'running' && step.status === 'running')
            }),
            message: step.message || ''
          };
          items.push(item);
        }
      });

    items.push({
      id: `item-start`,
      content: '',
      start: DateTime.fromISO(job.created).plus({ minute: offsetMinute }).toJSDate(),
      group: groups.toSorted((a, b) => (b.order || Infinity) - (a.order || Infinity))[0]?.id,
      title: 'start',
      type: 'box',
      className: 'timeline-item-start text-xs'
    });
    setExecutionTrace(sortedExecutionTrace);
    setItems(items);
    setGroups(groups);
  }, [job, originalStepWithoutWrapping, offsetMinute]);

  return { originalStepWithoutWrapping, executionTrace, items, groups };
};
